import { useState, useCallback, useRef } from 'react'
import { clsx } from 'clsx'
import { Upload, FileText, Wand2, AlertCircle } from 'lucide-react'
import { Modal, ModalFooter, Button, Spinner, Alert } from '@orientamos/ui'
import { extractTemplateFromText, type TemplateExtractionResult } from '@lib/api'
import mammoth from 'mammoth'

interface AITemplateImportModalProps {
  open: boolean
  onClose: () => void
  onApply: (result: TemplateExtractionResult) => void
}

type InputMode = 'text' | 'file'
type ProcessingState = 'idle' | 'extracting-html' | 'processing' | 'done' | 'error'

/**
 * Extract HTML from a DOCX file using mammoth (preserves structure)
 */
async function extractHtmlFromDOCX(file: File): Promise<string> {
  const arrayBuffer = await file.arrayBuffer()
  const result = await mammoth.convertToHtml({ arrayBuffer })
  return result.value
}

/**
 * CP-1252 to Unicode mapping for bytes 0x80-0x9F.
 * These bytes in CP-1252 do NOT map to the same Unicode code points,
 * so we need an explicit lookup for smart quotes, dashes, etc.
 */
const CP1252_MAP: Record<number, number> = {
  0x91: 0x2018, // '
  0x92: 0x2019, // '
  0x93: 0x201c, // "
  0x94: 0x201d, // "
  0x96: 0x2013, // –
  0x97: 0x2014, // —
  0x85: 0x2026, // …
}

/**
 * CP-1252 byte codes for Spanish/Latin characters (0xA0-0xFF range).
 * These map 1:1 to Unicode, so String.fromCharCode works directly.
 */
const SPANISH_CHARS = new Set([
  0xe1,
  0xe9,
  0xed,
  0xf3,
  0xfa, // áéíóú
  0xc1,
  0xc9,
  0xcd,
  0xd3,
  0xda, // ÁÉÍÓÚ
  0xf1,
  0xd1, // ñÑ
  0xfc,
  0xdc, // üÜ
  0xab,
  0xbb, // «»
  0xba,
  0xaa, // º ª
  0xbf,
  0xa1, // ¿¡
  0xb0, // ° (degree, used in "No." and addresses)
  0xb7, // · (middle dot)
])

/**
 * Extract raw text from a legacy .doc (OLE2 binary) file.
 *
 * Decodes the file bytes as CP-1252, keeping only printable ASCII
 * plus known Spanish accented characters. Extracts runs of 30+
 * characters to filter out binary noise.
 */
function extractTextFromDOC(arrayBuffer: ArrayBuffer): string {
  const bytes = new Uint8Array(arrayBuffer)

  const chars: string[] = []
  for (let i = 0; i < bytes.length; i++) {
    const b = bytes[i] as number
    if ((b >= 0x20 && b <= 0x7e) || b === 0x0a || b === 0x0d || b === 0x09) {
      // Standard ASCII printable + whitespace
      chars.push(String.fromCharCode(b))
    } else if (SPANISH_CHARS.has(b)) {
      // Known Spanish/Latin accented characters (0xA0-0xFF range, same in Unicode)
      chars.push(String.fromCharCode(b))
    } else if (b in CP1252_MAP) {
      // CP-1252 special chars (0x80-0x9F) mapped to correct Unicode
      chars.push(String.fromCharCode(CP1252_MAP[b] as number))
    } else {
      chars.push('\0')
    }
  }

  const text = chars.join('')
  const NUL = String.fromCharCode(0)
  const runs = text.split(new RegExp(`${NUL}+`)).filter((run) => run.trim().length >= 30)

  return runs
    .join('\n')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/**
 * Check if a file is a legacy .doc (OLE2) format by checking magic bytes
 */
function isOLE2File(arrayBuffer: ArrayBuffer): boolean {
  const bytes = new Uint8Array(arrayBuffer.slice(0, 8))
  // OLE2 magic: D0 CF 11 E0 A1 B1 1A E1
  return (
    bytes[0] === 0xd0 &&
    bytes[1] === 0xcf &&
    bytes[2] === 0x11 &&
    bytes[3] === 0xe0 &&
    bytes[4] === 0xa1 &&
    bytes[5] === 0xb1 &&
    bytes[6] === 0x1a &&
    bytes[7] === 0xe1
  )
}

export function AITemplateImportModal({ open, onClose, onApply }: AITemplateImportModalProps) {
  const [inputMode, setInputMode] = useState<InputMode>('file')
  const [textInput, setTextInput] = useState('')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [state, setState] = useState<ProcessingState>('idle')
  const [error, setError] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const handleClose = useCallback(() => {
    // Cancel any in-flight API request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setState('idle')
    setError(null)
    setTextInput('')
    setSelectedFile(null)
    onClose()
  }, [onClose])

  const handleFileDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024
      const validExtensions = ['.docx', '.doc']
      const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))

      if (file.size > MAX_FILE_SIZE) {
        setError('El archivo es demasiado grande. Maximo 10MB.')
        return
      }

      if (
        hasValidExtension ||
        file.type.includes('wordprocessingml') ||
        file.type === 'application/msword'
      ) {
        setSelectedFile(file)
        setError(null)
      } else {
        setError('Tipo de archivo no soportado. Use archivos .doc o .docx.')
      }
    }
  }, [])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const MAX_FILE_SIZE = 10 * 1024 * 1024
      if (file.size > MAX_FILE_SIZE) {
        setError('El archivo es demasiado grande. Maximo 10MB.')
        return
      }
      const validExtensions = ['.docx', '.doc']
      const hasValidExtension = validExtensions.some((ext) => file.name.toLowerCase().endsWith(ext))
      if (
        !hasValidExtension &&
        !file.type.includes('wordprocessingml') &&
        file.type !== 'application/msword'
      ) {
        setError('Tipo de archivo no soportado. Use archivos .doc o .docx.')
        return
      }
      setSelectedFile(file)
      setError(null)
    }
  }, [])

  const handleProcess = async () => {
    setError(null)

    try {
      let textContent: string

      if (inputMode === 'text') {
        if (!textInput.trim()) {
          setError('Por favor ingresa el texto del contrato')
          return
        }
        textContent = textInput
      } else {
        if (!selectedFile) {
          setError('Por favor selecciona un archivo')
          return
        }

        setState('extracting-html')

        const fileName = selectedFile.name.toLowerCase()
        const arrayBuffer = await selectedFile.arrayBuffer()
        const isLegacyDoc = isOLE2File(arrayBuffer)

        if (isLegacyDoc) {
          // Legacy .doc (OLE2 binary format) — extract raw text
          try {
            textContent = extractTextFromDOC(arrayBuffer)
          } catch (docError) {
            console.error('DOC extraction error:', docError)
            setError(
              'Error al leer el archivo .doc. Intenta abrir el archivo en Word, guardarlo como .docx, o copiar y pegar el texto.'
            )
            setState('idle')
            return
          }
        } else if (fileName.endsWith('.docx') || selectedFile.type.includes('wordprocessingml')) {
          // Modern .docx — use mammoth for HTML extraction
          try {
            textContent = await extractHtmlFromDOCX(selectedFile)
          } catch (docxError) {
            console.error('DOCX extraction error:', docxError)
            setError('Error al leer el archivo Word. Intenta copiar el texto manualmente.')
            setState('idle')
            return
          }
        } else {
          setError('Tipo de archivo no soportado. Use archivos .doc o .docx.')
          setState('idle')
          return
        }
      }

      if (!textContent || textContent.trim().length < 50) {
        setError('El contenido extraido es muy corto. Por favor verifica el archivo o el texto.')
        setState('idle')
        return
      }

      setState('processing')

      abortControllerRef.current = new AbortController()

      const result = await extractTemplateFromText(textContent)

      // Check if request was aborted (modal closed)
      if (abortControllerRef.current?.signal.aborted) return

      if (!result.success || !result.data) {
        setError(result.error || 'Error al procesar el documento')
        setState('error')
        return
      }

      if (!result.data.htmlContent) {
        setError('La IA no genero contenido HTML. Intenta de nuevo.')
        setState('error')
        return
      }

      setState('done')
      onApply(result.data)
      handleClose()
    } catch (err) {
      // Ignore abort errors
      if (err instanceof DOMException && err.name === 'AbortError') return
      console.error('Processing error:', err)
      setError('Error inesperado al procesar. Por favor intenta de nuevo.')
      setState('error')
    }
  }

  const renderIdleState = () => (
    <>
      {/* Input mode tabs */}
      <div className="flex border-b border-neutral-200 mb-4">
        <button
          type="button"
          onClick={() => setInputMode('file')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            inputMode === 'file'
              ? 'text-primary-600 border-primary-600'
              : 'text-neutral-500 border-transparent hover:text-neutral-700'
          )}
        >
          <Upload className="h-4 w-4 inline mr-2" />
          Subir Archivo
        </button>
        <button
          type="button"
          onClick={() => setInputMode('text')}
          className={clsx(
            'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
            inputMode === 'text'
              ? 'text-primary-600 border-primary-600'
              : 'text-neutral-500 border-transparent hover:text-neutral-700'
          )}
        >
          <FileText className="h-4 w-4 inline mr-2" />
          Pegar Texto
        </button>
      </div>

      {inputMode === 'file' ? (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Sube un archivo Word (.doc o .docx) con el contrato que deseas convertir en plantilla.
          </p>
          <div
            onDrop={handleFileDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className={clsx(
              'border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors',
              selectedFile
                ? 'border-primary-300 bg-primary-50'
                : 'border-neutral-300 hover:border-primary-400 hover:bg-neutral-50'
            )}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".docx,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center justify-center gap-3">
                <FileText className="h-8 w-8 text-primary-600" />
                <div className="text-left">
                  <p className="font-medium text-neutral-900">{selectedFile.name}</p>
                  <p className="text-sm text-neutral-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              </div>
            ) : (
              <>
                <Upload className="h-10 w-10 mx-auto text-neutral-400 mb-3" />
                <p className="text-neutral-600">Arrastra un archivo aqui</p>
                <p className="text-sm text-neutral-500 mt-1">o haz clic para seleccionar</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Pega el texto o HTML del contrato que deseas convertir en plantilla.
          </p>
          <textarea
            value={textInput}
            onChange={(e) => setTextInput(e.target.value)}
            placeholder="Pega aqui el texto del contrato..."
            className="w-full h-48 px-4 py-3 border border-neutral-300 rounded-lg resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>
      )}

      {error && <Alert variant="danger">{error}</Alert>}
    </>
  )

  const renderProcessingState = () => (
    <div className="py-12 text-center">
      <Spinner size="lg" className="mx-auto mb-4" />
      <p className="text-lg font-medium text-neutral-900">
        {state === 'extracting-html'
          ? 'Extrayendo contenido del archivo...'
          : 'Generando plantilla con IA...'}
      </p>
      <p className="text-sm text-neutral-500 mt-2">
        {state === 'processing'
          ? 'Analizando el contrato y creando placeholders. Esto puede tomar hasta un minuto.'
          : 'Esto puede tomar unos segundos'}
      </p>
    </div>
  )

  const renderErrorState = () => (
    <div className="py-8 text-center">
      <AlertCircle className="h-12 w-12 mx-auto text-danger-500 mb-4" />
      <p className="text-lg font-medium text-neutral-900 mb-2">Error al procesar</p>
      <p className="text-sm text-neutral-600">{error}</p>
    </div>
  )

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Importar Plantilla con IA"
      description="Convierte un contrato existente en una plantilla reutilizable con campos dinamicos"
      size="lg"
      persistent
    >
      {state === 'idle' && renderIdleState()}
      {(state === 'extracting-html' || state === 'processing') && renderProcessingState()}
      {state === 'error' && renderErrorState()}

      <ModalFooter>
        {state === 'idle' && (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={handleProcess}
              disabled={inputMode === 'text' ? !textInput.trim() : !selectedFile}
            >
              <Wand2 className="h-4 w-4 mr-2" />
              Generar Plantilla
            </Button>
          </>
        )}

        {state === 'error' && (
          <>
            <Button variant="outline" onClick={handleClose}>
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setState('idle')
                setError(null)
              }}
            >
              Intentar de nuevo
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}
